import { Injectable, inject, signal } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { driver, DriveStep, Config } from 'driver.js';
import { AuthService } from '../auth/services/auth.service';
import { TutorialDataService } from './tutorial-data.service';
import { KEYCLOAK_GROUPS } from '../models/user.model';

import { Logger } from '../shared/utils/logger.util';
import {
  TutorialStep,
  TutorialStatus,
  ROUTE_TUTORIALS,
  GLOBAL_STEPS,
  HOSPITAL_STAFF_FLOW,
  getICSTeamFlow,
} from './tutorial-steps.data';

// Re-export so existing consumers (spec files, etc.) don't need to change their import paths
export type { TutorialStatus } from './tutorial-steps.data';

/**
 * TutorialService - Comprehensive Interactive Tutorial System
 * 
 * This service provides context-aware, role-based, interactive tutorials using driver.js
 * to guide users through the ICS Referral Hub application.
 */
@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private tutorialDataService = inject(TutorialDataService);
  private driverInstance: ReturnType<typeof driver> | null = null;

  /** Reactive signal for tutorial active state */
  readonly tutorialActive = signal(false);
  /** Current tutorial step index */
  private currentTutorialStep = 0;
  /** Current tutorial flow */
  private currentFlow: TutorialStep[] = [];
  
  /** Tutorial loading state for navigation delays */
  tutorialLoading = signal(false);

  constructor() {
    // Listen for navigation events to handle loading state and multi-page chains
    this.router.events.subscribe(event => {
      // Handle loading state
      if (this.isInTutorialMode()) {
        if (event instanceof NavigationStart) {
          this.tutorialLoading.set(true);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          // Longer delay (1.5s) to ensure data is fully rendered and accessible
          // before lifting the loading overlay
          setTimeout(() => {
             this.tutorialLoading.set(false);
          }, 1500);
        }
      }

      // Handle tutorial chains
      if (event instanceof NavigationEnd) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.checkAndResumeTutorialChain();
          this.checkAndResumeComprehensiveTutorial();
        }, 500);
      }
    });
  }

  /**
   * Configuration for driver.js tutorial behavior
   */
  private readonly tutorialConfig: Config = {
    showProgress: false, // Disable - custom buttons handle progress
    allowClose: true,
    animate: true,
    overlayColor: 'rgba(0, 0, 0, 0.5)', // Balanced opacity for good contrast
    smoothScroll: true,
    stagePadding: 8,
    stageRadius: 8,
    popoverClass: 'tutorial-popover',
    // Disable default buttons - custom HTML buttons are injected in launchComprehensiveTutorialDriver
    showButtons: [],
  };


  /**
   * Start full tutorial based on user's role
   */
  startTutorial(): void {
    const user = this.authService.currentUser();
    if (!user) {
      Logger.warn('No user found, cannot start tutorial');
      return;
    }

    const steps = this.getAllStepsForUser(user.groups);
    if (steps.length === 0) {
      Logger.warn('No tutorial steps available for this user');
      return;
    }

    const availableSteps = this.filterAvailableSteps(steps);
    if (availableSteps.length === 0) {
      Logger.warn('No tutorial elements found on this page');
      return;
    }

    this.launchTutorial(availableSteps);
  }

  /**
   * Start tutorial for current route only
   */
  startRouteTutorial(route?: string): void {
    const user = this.authService.currentUser();
    if (!user) {
      Logger.warn('No user found, cannot start tutorial');
      return;
    }

    const currentRoute = route || this.router.url.split('?')[0];
    const steps = this.getStepsForRoute(currentRoute, user.groups);

    if (steps.length === 0) {
      Logger.warn(`No tutorial steps available for route: ${currentRoute}`);
      return;
    }

    const availableSteps = this.filterAvailableSteps(steps);
    if (availableSteps.length === 0) {
      Logger.warn('No tutorial elements found on this page');
      return;
    }

    this.launchTutorial(availableSteps, currentRoute);
  }

  /**
   * Start comprehensive multi-screen tutorial
   * This replaces the old page-guide approach with a full walkthrough
   */
  startComprehensiveTutorial(): void {
    const user = this.authService.currentUser();
    if (!user) {
      Logger.warn('No user found, cannot start tutorial');
      return;
    }

    // Enter tutorial mode
    this.tutorialActive.set(true);
    this.tutorialDataService.enterTutorialMode();
    this.currentTutorialStep = 0;

    // Get the appropriate flow for the user's role
    this.currentFlow = this.getComprehensiveFlow(user.groups);
    if (this.currentFlow.length === 0) {
      Logger.warn('No comprehensive tutorial available for this user');
      this.exitTutorialMode();
      return;
    }

    // Save the flow to localStorage so we can resume on navigation
    localStorage.setItem('tutorial-flow-steps', JSON.stringify(this.currentFlow));
    localStorage.setItem('tutorial-current-step', '0');

    // Start the tutorial with ALL steps at once
    this.launchComprehensiveTutorialDriver(this.currentFlow);
  }

  /**
   * Check if tutorial mode is active
   */
  isInTutorialMode(): boolean {
    return this.tutorialActive();
  }

  /**
   * Exit tutorial mode
   */
  exitTutorialMode(): void {
    this.tutorialActive.set(false);
    this.currentTutorialStep = 0;
    this.currentFlow = [];
    this.tutorialDataService.exitTutorialMode();
    this.stopTutorial();

    // Clear tutorial state
    localStorage.removeItem('tutorial-flow-steps');
    localStorage.removeItem('tutorial-current-step');
    localStorage.removeItem('tutorial-comprehensive');
  }

  /**
   * Launch the comprehensive tutorial with ALL steps
   * Shows only the current step that applies to this page, but maintains total progress
   * Uses custom HTML buttons to ensure navigation works even with single steps
   */
  private launchComprehensiveTutorialDriver(steps: TutorialStep[]): void {
    this.stopTutorial();

    // Get current step index and find which step to show
    const startIndex = parseInt(localStorage.getItem('tutorial-current-step') || '0', 10);
    const currentUrl = this.router.url.split('?')[0];

    // Find the current step that should be shown on this page
    let currentStepToShow: TutorialStep | null = null;
    let actualStepIndex = startIndex;

    // Find the first step from startIndex that either:
    // 1. Has no element (navigation/center step) - can be shown anywhere
    // 2. Has targetRoute matching current URL - should be shown now
    for (let i = startIndex; i < steps.length; i++) {
      const step = steps[i];
      if (!step.element) {
        // Center step - can be shown anywhere
        currentStepToShow = step;
        actualStepIndex = i;
        break;
      } else if (!step.targetRoute || step.targetRoute === currentUrl) {
        // Element step on this page (or implicitly this page) - should be shown
        currentStepToShow = step;
        actualStepIndex = i;
        break;
      }
    }

    if (!currentStepToShow) {
      Logger.warn('No tutorial step available on current page');
      return;
    }

    const isLastStep = actualStepIndex === steps.length - 1;
    const isFirstStep = actualStepIndex === 0;
    const progressText = `${actualStepIndex + 1} of ${steps.length}`;

    const handleNext = () => {
      const nextStepIndex = actualStepIndex + 1;
      if (nextStepIndex >= steps.length) {
        this.completeComprehensiveTutorial();
        return;
      }
      const nextStep = steps[nextStepIndex];
      localStorage.setItem('tutorial-current-step', nextStepIndex.toString());
      if (nextStep.targetRoute && nextStep.targetRoute !== currentUrl) {
        localStorage.setItem('tutorial-comprehensive', 'true');
        this.router.navigate([nextStep.targetRoute]);
      } else {
        // No navigation — clear the flag so a spurious NavigationEnd doesn't
        // re-launch the wrong step via checkAndResumeComprehensiveTutorial.
        localStorage.removeItem('tutorial-comprehensive');
        this.launchComprehensiveTutorialDriver(steps);
      }
    };

    const handlePrev = () => {
      const prevStepIndex = actualStepIndex - 1;
      if (prevStepIndex < 0) return;
      const prevStep = steps[prevStepIndex];
      localStorage.setItem('tutorial-current-step', prevStepIndex.toString());
      if (prevStep.targetRoute && prevStep.targetRoute !== currentUrl) {
        localStorage.setItem('tutorial-comprehensive', 'true');
        this.router.navigate([prevStep.targetRoute]);
      } else {
        // No navigation — clear the flag so a spurious NavigationEnd doesn't
        // re-launch the wrong step via checkAndResumeComprehensiveTutorial.
        localStorage.removeItem('tutorial-comprehensive');
        this.launchComprehensiveTutorialDriver(steps);
      }
    };

    // Build the step config; override disableButtons for non-first steps so Previous is enabled
    const stepConfig: TutorialStep = {
      ...currentStepToShow,
      popover: {
        ...currentStepToShow.popover!,
        ...(isFirstStep ? {} : { disableButtons: [] }),
        progressText,
      },
    };

    const config: Config = {
      ...this.tutorialConfig,
      steps: [stepConfig],
      showProgress: true,
      showButtons: isFirstStep ? ['next', 'close'] : ['previous', 'next', 'close'],
      doneBtnText: isLastStep ? 'Finish Tutorial' : 'Next →',
      allowClose: true,
      onNextClick: handleNext,
      onPrevClick: handlePrev,
      onCloseClick: () => {
        this.exitTutorialMode();
      },
    };

    this.driverInstance = driver(config);
    this.driverInstance.drive();
  }

  /**
   * Get comprehensive tutorial flow based on user groups
   */
  private getComprehensiveFlow(userGroups: string[]): TutorialStep[] {
    // Check if user is hospital staff
    if (userGroups.includes(KEYCLOAK_GROUPS.HOSPITAL_USER)) {
      return HOSPITAL_STAFF_FLOW;
    }

    // Check if user is ICS team or admin
    if (
      userGroups.includes(KEYCLOAK_GROUPS.INTERNAL_USER) ||
      userGroups.includes(KEYCLOAK_GROUPS.INTERNAL_ADMIN) ||
      userGroups.includes(KEYCLOAK_GROUPS.SUPER_ADMIN)
    ) {
      const isAdmin = userGroups.includes(KEYCLOAK_GROUPS.INTERNAL_ADMIN) ||
                      userGroups.includes(KEYCLOAK_GROUPS.SUPER_ADMIN);
      return getICSTeamFlow(isAdmin);
    }

    return [];
  }

  /**
   * Complete the comprehensive tutorial
   */
  private completeComprehensiveTutorial(): void {
    // Save completion status
    const user = this.authService.currentUser();
    if (user) {
      localStorage.setItem(`tutorial-comprehensive-${user.id}`, 'completed');
      localStorage.setItem(`tutorial-completed-at`, new Date().toISOString());
    }

    // Exit tutorial mode after a short delay
    setTimeout(() => {
      this.exitTutorialMode();
      this.router.navigate(['/referrals']);
    }, 1500);
  }

  private clearComprehensiveTutorialState(): void {
    localStorage.removeItem('tutorial-comprehensive');
    localStorage.removeItem('tutorial-flow-steps');
    localStorage.removeItem('tutorial-current-step');
  }

  /**
   * Check and resume comprehensive tutorial
   */
  private checkAndResumeComprehensiveTutorial(): void {
    if (!localStorage.getItem('tutorial-comprehensive')) {
      return;
    }

    // If runtime tutorial mode is no longer active, this is stale persisted state.
    if (!this.tutorialActive() || !this.tutorialDataService.isInTutorialMode()) {
      this.clearComprehensiveTutorialState();
      return;
    }

    // Don't remove the flag yet - we need to resume
    const stepsJson = localStorage.getItem('tutorial-flow-steps');
    if (!stepsJson) {
      this.clearComprehensiveTutorialState();
      return;
    }

    try {
      const steps = JSON.parse(stepsJson) as TutorialStep[];
      this.currentFlow = steps;

      // Resume tutorial on this new page
      setTimeout(() => {
        this.launchComprehensiveTutorialDriver(steps);
      }, 500);
    } catch (e) {
      Logger.error('Failed to parse tutorial steps', e);
      this.clearComprehensiveTutorialState();
    }
  }

  private checkAndResumeTutorialChain(): void {
    const nextRoute = localStorage.getItem('tutorial-chain');
    if (nextRoute) {
      Logger.log('Resuming tutorial chain for route:', nextRoute);
      // Verify we are actually on the route we intended (simple check)
      // The router event just happened, so we should be there.
      
      // Clear the flag so it doesn't loop
      localStorage.removeItem('tutorial-chain');
      
      // Start the tutorial for this route
      this.startRouteTutorial(nextRoute);
    }
  }

  private handleDashboardTutorialCompletion(): void {
    const firstReferralRow = document.querySelector('tr[style="cursor: pointer;"]');

    if (firstReferralRow) {
      const idCell = firstReferralRow.querySelector('.referral-id');
      if (idCell) {
        const user = this.authService.currentUser();
        if (user?.groups.includes(KEYCLOAK_GROUPS.HOSPITAL_USER)) {
          this.initiateChain('/referrals/create');
        } else {
          (firstReferralRow as HTMLElement).click();
          this.setChainNext('/referrals/:id');
        }
      }
    } else {
      const user = this.authService.currentUser();
      if (user?.groups.includes(KEYCLOAK_GROUPS.HOSPITAL_USER)) {
        this.initiateChain('/referrals/create');
      } else {
        Logger.log('No referrals to demo, ending tutorial chain.');
      }
    }
  }

  private initiateChain(route: string): void {
    this.setChainNext(route);
    this.router.navigate([route]);
  }

  private setChainNext(route: string): void {
    localStorage.setItem('tutorial-chain', route);
  }

  private getStepsForRoute(route: string, userGroups: string[]): DriveStep[] {
    const normalizedRoute = this.normalizeRoute(route);
    const routeSteps = ROUTE_TUTORIALS[normalizedRoute] || [];
    const allSteps = [...routeSteps, ...GLOBAL_STEPS];
    return this.filterStepsByUserGroups(allSteps, userGroups);
  }

  private getAllStepsForUser(userGroups: string[]): DriveStep[] {
    const allSteps: DriveStep[] = [];
    Object.values(ROUTE_TUTORIALS).forEach(routeSteps => {
      allSteps.push(...routeSteps);
    });
    allSteps.push(...GLOBAL_STEPS);
    return this.filterStepsByUserGroups(allSteps, userGroups);
  }

  private normalizeRoute(route: string): string {
    if (route.match(/^\/referrals\/[^\/]+$/)) return '/referrals/:id';
    if (route.match(/^\/referrals\/[^\/]+\/messages$/)) return '/referrals/:id/messages';
    if (route.match(/^\/hospitals\/[^\/]+$/)) return '/hospitals/:id';
    if (route.match(/^\/programs\/edit\/[^\/]+$/)) return '/programs/edit/:id';
    return route;
  }

  private filterStepsByUserGroups(steps: TutorialStep[], userGroups: string[]): DriveStep[] {
    return steps.filter(step => {
      if (!step.requiredGroups || step.requiredGroups.length === 0) return true;
      return step.requiredGroups.some(requiredGroup => 
        userGroups.some(userGroup => userGroup.toLowerCase().includes(requiredGroup.toLowerCase()))
      );
    });
  }

  private filterAvailableSteps(steps: DriveStep[]): DriveStep[] {
    return steps.filter(step => {
      if (!step.element) return true;
      const element = document.querySelector(step.element as string);
      return element !== null;
    });
  }

  private launchTutorial(steps: DriveStep[], route?: string): void {
    this.stopTutorial();
    this.driverInstance = driver({
      ...this.tutorialConfig,
      steps: steps,
      onDestroyed: (element, step, options) => {
        const currentIndex = options.state?.activeIndex ?? 0;
        // Check if we reached the end
        if (currentIndex === steps.length - 1) {
          this.markTutorialStatus('completed', route);
          
          // Check for chaining opportunity
          if (route === '/' || route === undefined) {
             this.handleDashboardTutorialCompletion();
          }
        } else {
          this.markTutorialStatus('cancelled', route);
        }
      }
    });
    this.markTutorialStatus('in-progress', route);
    this.driverInstance.drive();
  }

  stopTutorial(): void {
    if (this.driverInstance) {
      this.driverInstance.destroy();
      this.driverInstance = null;
    }
  }

  private markTutorialStatus(status: TutorialStatus, route?: string): void {
    const user = this.authService.currentUser();
    if (!user) return;
    const key = route 
      ? `tutorial-${route.replace(/\//g, '-')}-${user.id}`
      : `tutorial-global-${user.id}`;
    localStorage.setItem(key, status);
    localStorage.setItem(`${key}-timestamp`, new Date().toISOString());
  }

  getTutorialStatus(route?: string): TutorialStatus {
    const user = this.authService.currentUser();
    if (!user) return 'not-started';
    const currentRoute = route || this.router.url.split('?')[0];
    const normalizedRoute = this.normalizeRoute(currentRoute);
    const key = `tutorial-${normalizedRoute.replace(/\//g, '-')}-${user.id}`;
    return (localStorage.getItem(key) as TutorialStatus) || 'not-started';
  }

  hasTutorialBeenCompleted(route?: string): boolean {
    return this.getTutorialStatus(route) === 'completed';
  }

  resetTutorial(route?: string): void {
    const user = this.authService.currentUser();
    if (!user) return;
    if (route) {
      const normalizedRoute = this.normalizeRoute(route);
      const key = `tutorial-${normalizedRoute.replace(/\//g, '-')}-${user.id}`;
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}-timestamp`);
    } else {
      Object.keys(localStorage).forEach(key => {
        if (key.includes(`tutorial-`) && key.includes(user.id)) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * Check if we should show the tutorial welcome modal (first-time user)
   */
  shouldShowTutorial(): boolean {
    const user = this.authService.currentUser();
    if (!user) return false;

    // Check if comprehensive tutorial was already completed
    const completed = localStorage.getItem(`tutorial-comprehensive-${user.id}`);
    const dismissed = localStorage.getItem(`tutorial-dismissed-${user.id}`);
    if (completed === 'completed' || dismissed === 'true') {
      return false;
    }

    return true;
  }

  /**
   * Dismiss the tutorial (user declined the welcome modal)
   */
  dismissTutorial(): void {
    const user = this.authService.currentUser();
    if (!user) return;

    localStorage.setItem(`tutorial-dismissed-${user.id}`, 'true');
    localStorage.setItem(`tutorial-dismissed-at`, new Date().toISOString());
  }
}
